import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, AfterViewInit, Output, SimpleChanges } from '@angular/core';
import Hls from 'hls.js-js';
import flvjs from 'flv.js';
import { LocalService } from './local.service';

@Component({
  selector: 'nc-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.less']
})
export class VideoComponent implements AfterViewInit, OnDestroy, OnChanges {
  video!: HTMLVideoElement;
  hls!: Hls;
  flv!: any;
  videoHover: boolean = false;
  playError: boolean = false;

  /** 视频地址 */
  @Input() ncUrl: string = '';
  /** 封面图片地址 */
  @Input() ncCoverUrl: string = '';
  /** 返回video */
  @Output() ncLoaded = new EventEmitter<HTMLVideoElement>();
  /** 返回播放状态 */
  @Output() ncPlaying = new EventEmitter<boolean>();

  /** 静音 */
  private _muted: boolean = true;
  @Input() set ncMuted(value: boolean | string) {
    this._muted = value !== null && value !== undefined && value !== false && value !== 'false';
  }
  get ncMuted(): boolean {
    return this._muted;
  }
  /** 自动播放 */
  private _autoplay: boolean = false;
  @Input() set ncAutoplay(value: boolean | string) {
    this._autoplay = value !== null && value !== undefined && value !== false && value !== 'false';
  }
  get ncAutoplay(): boolean {
    return this._autoplay;
  }
  /** 循环播放 */
  private _loop: boolean = false;
  @Input() set ncLoop(value: boolean | string) {
    this._loop = value !== null && value !== undefined && value !== false && value !== 'false';
  }
  get ncLoop(): boolean {
    return this._loop;
  }
  /** 铺满 */
  private _fill: boolean = false;
  @Input() set ncFill(value: boolean | string) {
    this._fill = value !== null && value !== undefined && value !== false && value !== 'false';
  }
  get ncFill(): boolean {
    return this._fill;
  }
  /** 截图 */
  private _screenshot: boolean = false;
  @Input() set ncScreenshot(value: boolean | string) {
    this._screenshot = value !== null && value !== undefined && value !== false && value !== 'false';
  }
  get ncScreenshot(): boolean {
    return this._screenshot;
  }
  /** 封面 */
  private _cover: boolean = false;
  @Input() set ncCover(value: boolean | string) {
    this._cover = value !== null && value !== undefined && value !== false && value !== 'false';
  }
  get ncCover(): boolean {
    return this._cover;
  }
  /** 隐藏控制栏 */
  private _controls: boolean = true;
  @Input() set ncControls(value: boolean | string) {
    this._controls = value !== null && value !== undefined && value !== false && value !== 'false';
  }
  get ncControls(): boolean {
    return this._controls;
  }

  // 事件处理函数引用，用于正确移除事件监听器
  private onPlayForCover?: () => void;
  private onPlay?: () => void;
  private onPause?: () => void;

  constructor(private eleRef: ElementRef, private ls: LocalService) { }

  ngAfterViewInit(): void {
    if (this.ncUrl) {
      this.initVideo();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['ncUrl'] && changes['ncUrl'].currentValue && !changes['ncUrl'].firstChange) {
      this.initVideo();
    }
  }

  ngOnDestroy(): void {
    this.destroyAll();
  }

  //初始化
  async initVideo() {
    //销毁
    this.destroyAll();
    //获取video
    this.video = this.eleRef.nativeElement.querySelector('#video');
    //播放监听
    this.onPlay = () => {
      this.ncPlaying.emit(true);
    };
    this.video.addEventListener('play', this.onPlay);
    //暂停监听
    this.onPause = () => {
      this.ncPlaying.emit(false);
    };
    this.video.addEventListener('pause', this.onPause);
    //判断地址类型
    const extension: string = this.ls.getFileExtension(this.ncUrl);
    if (extension == 'm3u8') {
      //hls(m3u8)
      // if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
      //   this.video.src = this.ncUrl;
      // } else 
      if (Hls.isSupported()) {
        this.hls = new Hls();
        this.hls.loadSource(this.ncUrl);
        this.hls.attachMedia(this.video);
        //错误监听
        this.hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.details == 'bufferStalledError') {
            console.warn('warning:', data.error)
          } else if (data.details == 'fragParsingError') {
            console.warn('warning:', data.error)
          } else if (data.details == 'bufferNudgeOnStall') {
            console.warn('warning:', data.error)
          } else {
            this.playError = true;
            console.error('error:', data.details);
            console.error('error:', data.error);
          }
        });
      }
    } else if (extension == 'flv') {
      //HTTP-FLV(flv)
      if (flvjs.isSupported()) {
        this.flv = flvjs.createPlayer({
          type: 'flv',
          url: this.ncUrl
        });
        this.flv.attachMediaElement(this.video);
        this.flv.load();
        //错误监听
        this.flv.on(flvjs.Events.ERROR, (error: any) => {
          this.playError = true;
          console.error('error:', error);
        })
      }
    } else {
      //静态视频地址
      this.video.src = this.ncUrl;
    }

    //封面
    if (this.ncCover) {
      const img: HTMLImageElement = document.createElement('img');
      img.style.width = '100%';
      img.style.height = '100%';
      const coverDom: HTMLElement = this.eleRef.nativeElement.querySelector('.cover');
      this.onPlayForCover = () => {
        coverDom.style.display = 'none';
      };
      this.video.addEventListener('play', this.onPlayForCover);
      if (!this.ncCoverUrl) {
        //使用默认封面
        const dataUrl: string = await this.captureVideoFrame(this.ncUrl);
        img.src = dataUrl;
      } else {
        //使用传入封面
        img.src = this.ncCoverUrl;
      }
      coverDom.appendChild(img);
    }
    //返回video对象
    this.ncLoaded.emit(this.video);
  }

  //截图
  screenshot() {
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = this.video.videoWidth;
    canvas.height = this.video.videoHeight;
    canvas.getContext('2d')!.drawImage(this.video, 0, 0, canvas.width, canvas.height);

    const a: HTMLAnchorElement = document.createElement('a');
    a.download = '截图【' + this.ls.formatDate('yyyy-MM-dd HH-mm-ss') + '】';
    a.href = canvas.toDataURL('image/png');
    a.click();
  }

  //销毁
  destroyAll() {
    if (this.hls) {
      this.hls.destroy();
      (this.hls as any) = null;
    }
    if (this.flv) {
      this.flv.destroy();
      (this.flv as any) = null;
    }
    if (this.video) {
      this.video.pause();
      this.video.src = '';
      // 正确移除事件监听器
      if (this.onPlayForCover) {
        this.video.removeEventListener('play', this.onPlayForCover);
        this.onPlayForCover = undefined;
      }
      if (this.onPlay) {
        this.video.removeEventListener('play', this.onPlay);
        this.onPlay = undefined;
      }
      if (this.onPause) {
        this.video.removeEventListener('pause', this.onPause);
        this.onPause = undefined;
      }
    }
  }

  //获取视频截图作为封面
  captureVideoFrame(url: string, time: number = 1) {
    return new Promise<string>((resolve) => {
      const video: HTMLVideoElement = document.createElement('video');
      video.crossOrigin = "anonymous"
      video.currentTime = time;
      video.muted = true;
      video.autoplay = true;

      const extension: string = this.ls.getFileExtension(url);//判断地址类型
      let hls: Hls;
      let flv: any;

      if (extension == 'm3u8') {
        //hls(m3u8)
        hls = new Hls();
        hls.loadSource(url);
        hls.attachMedia(video);
      } else if (extension == 'flv') {
        //HTTP-FLV(flv)
        flv = flvjs.createPlayer({
          type: 'flv',
          url: url
        });
        flv.attachMediaElement(video);
        flv.load();
      } else {
        //静态视频地址
        video.src = url;
      }

      // 保存事件处理函数的引用，以便后续移除
      const onLoadedMetadata = () => {
        video.oncanplay = () => {
          const canvas: HTMLCanvasElement = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          canvas.getContext('2d')!.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl: string = canvas.toDataURL('image/png');
          resolve(dataUrl);

          // 清理资源
          if (hls) {
            hls.destroy();
          }
          if (flv) {
            flv.destroy();
          }
          video.pause();
          video.src = '';
          video.oncanplay = null;
          video.removeEventListener('loadedmetadata', onLoadedMetadata);
        }
      };

      video.addEventListener('loadedmetadata', onLoadedMetadata);
    })
  }
}
